import * as React from "react"
import {
  Button,
  Modal,
  ModalCard,
  StyledModal,
  StyledModalActions,
  StyledModalBody,
  StyledModalHeader,
  Text,
  useShowSuccessToast,
} from "gatsby-interface"
import { ui as uiText, deleteCDN as text } from "@modules/locales/default.json"
import { CdnIntegration, CdnVendor } from "@modules/graphql/types"
import {
  SupportedCdnIntegrationsDocument,
  SupportedCdnIntegrationsQuery,
  SupportedCdnIntegrationsQueryVariables,
  useDeleteCdnFromSiteByVendorMutation,
} from "../queries.generated"
import { cdnVendorNames } from "../constants"
import {
  commonMessageTags,
  FormattedMessage,
  interpolateMessage,
} from "@modules/locales"
import { StyledForm } from "@modules/ui/components/Primitives"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"

export type CdnDeleteModalProps = {
  siteId: string
  siteName: string
  onClose: () => void
  cdn: CdnIntegration
}

export function CdnDeleteModal({
  cdn,
  onClose,
  siteName,
  siteId,
}: CdnDeleteModalProps) {
  const cdnVendor = cdn.vendor
  const vendorName = cdnVendorNames[cdnVendor]

  const [deleteMutation] = useDeleteCdnIntegration()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <Modal
      isOpen={Boolean(cdn)}
      aria-label={text.headers.deleteIntegration}
      onDismiss={onClose}
      type="error"
    >
      <ModalCard>
        <StyledModal variant="WARNING">
          <StyledModalHeader onCloseButtonClick={onClose}>
            {text.headers.deleteIntegration}
          </StyledModalHeader>
          <StyledModalBody>
            <Text>
              <FormattedMessage<"cdnVendorName" | "siteName", "strong">
                message={text.messages.areYouSure}
                values={{
                  cdnVendorName: vendorName,
                  siteName,
                }}
                tags={commonMessageTags}
              />
            </Text>
            <StyledForm
              onSubmit={e => {
                e.preventDefault()
                deleteMutation(siteId, cdnVendor)
                  .then(() => {
                    onClose()
                    showSuccessToast(
                      interpolateMessage<"cdnVendorName">(
                        text.messages.cdnIntegrationRemoved,
                        {
                          cdnVendorName: vendorName,
                        }
                      )
                    )
                  })
                  .catch(err => setError(err))
              }}
            >
              {errorAlert}
              <StyledModalActions>
                <Button
                  variant="SECONDARY"
                  tone="NEUTRAL"
                  type="button"
                  onClick={onClose}
                >
                  {uiText.actions.cancel}
                </Button>
                <Button variant="PRIMARY" tone="DANGER" type="submit">
                  {text.actions.delete}
                </Button>
              </StyledModalActions>
            </StyledForm>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}

function useDeleteCdnIntegration() {
  const [mutate, info] = useDeleteCdnFromSiteByVendorMutation()

  const deleteCdnIntegration = (siteId: string, vendor: CdnVendor) => {
    return mutate({
      variables: {
        siteId: siteId,
        vendor: vendor,
      },
      update: cache => {
        const integrationsFromCache = cache.readQuery<
          SupportedCdnIntegrationsQuery,
          SupportedCdnIntegrationsQueryVariables
        >({
          query: SupportedCdnIntegrationsDocument,
          variables: {
            siteId: siteId,
          },
        })

        const updatedIntegrations = integrationsFromCache?.supportedCDNIntegrations?.map(
          item => {
            if (item.vendor === vendor) {
              return {
                ...item,
                connected: false,
                config: {},
              }
            }
            return item
          }
        )

        cache.writeQuery<
          SupportedCdnIntegrationsQuery,
          SupportedCdnIntegrationsQueryVariables
        >({
          query: SupportedCdnIntegrationsDocument,
          variables: {
            siteId: siteId,
          },
          data: {
            ...integrationsFromCache,
            supportedCDNIntegrations: updatedIntegrations,
          },
        })
      },
    })
  }

  return [deleteCdnIntegration, info] as const
}
